import React from 'react';
import { Box, Typography, Tooltip, Paper } from '@mui/material';

interface HeatmapDay {
  date: string;
  count: number;
  dayOfWeek: number;
}

interface ActivityHeatmapProps {
  data: HeatmapDay[];
  maxCount: number;
}

/**
 * Activity Heatmap Component
 * GitHub-style calendar heatmap showing daily event activity
 * Stretches to fill the full width of its container
 */
export const ActivityHeatmap: React.FC<ActivityHeatmapProps> = ({ data, maxCount }) => {
  const gap = '3px';

  const getColor = (count: number): string => {
    if (count === 0) return '#ebedf0';
    const intensity = Math.min(count / maxCount, 1);
    if (intensity < 0.25) return '#fff4cc';
    if (intensity < 0.5) return '#ffe066';
    if (intensity < 0.75) return '#ffd633';
    return '#FFB800';
  };

  // Heatmap days are 'YYYY-MM-DD' in UTC (the backend buckets by UTC day).
  // Parse them as UTC and render in UTC so the grid never shifts a day in
  // western time zones.
  const parseDay = (dateString: string): Date =>
    /^\d{4}-\d{2}-\d{2}$/.test(dateString) ? new Date(`${dateString}T00:00:00Z`) : new Date(dateString);

  const formatDate = (dateString: string): string => {
    return parseDay(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  };

  // Group data by week
  const weeks: HeatmapDay[][] = [];
  let currentWeek: HeatmapDay[] = [];

  const firstDayOfWeek = data[0]?.dayOfWeek || 0;
  for (let i = 0; i < firstDayOfWeek; i++) {
    currentWeek.push({ date: '', count: 0, dayOfWeek: i });
  }

  data.forEach((day) => {
    currentWeek.push(day);
    if (day.dayOfWeek === 6 || day === data[data.length - 1]) {
      weeks.push([...currentWeek]);
      currentWeek = [];
    }
  });

  // Cell style: square, fills available width per column
  const cellSx = {
    width: '100%',
    aspectRatio: '1',
    borderRadius: '2px',
    border: '1px solid rgba(0,0,0,0.08)',
  };

  return (
    <Box>
      <Box display="flex" gap={gap}>
        {/* Day labels column — fixed narrow width */}
        <Box
          display="flex"
          flexDirection="column"
          gap={gap}
          sx={{ width: 28, flexShrink: 0 }}
        >
          {/* Spacer for month label row */}
          <Box sx={{ aspectRatio: '1', width: '100%' }} />
          {Array.from({ length: 7 }).map((_, i) => (
            <Box key={i} sx={{ ...cellSx, border: 'none', display: 'flex', alignItems: 'center' }}>
              {(i === 1 || i === 3 || i === 5) && (
                <Typography variant="caption" sx={{ fontSize: 9, color: 'text.secondary', lineHeight: 1 }}>
                  {['', 'Mon', '', 'Wed', '', 'Fri', ''][i]}
                </Typography>
              )}
            </Box>
          ))}
        </Box>

        {/* Heatmap grid — each week column is flex: 1 */}
        {weeks.map((week, weekIndex) => (
          <Box key={weekIndex} display="flex" flexDirection="column" gap={gap} flex={1} minWidth={0}>
            {/* Month label row */}
            {weekIndex === 0 || (week[0]?.date && parseDay(week[0].date).getUTCDate() <= 7) ? (
              <Box sx={{ ...cellSx, border: 'none', display: 'flex', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ fontSize: 9, color: 'text.secondary', lineHeight: 1 }} noWrap>
                  {week[0]?.date ? parseDay(week[0].date).toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }) : ''}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ ...cellSx, border: 'none' }} />
            )}

            {/* 7 day cells */}
            {Array.from({ length: 7 }).map((_, dayIndex) => {
              const day = week[dayIndex];
              if (!day || !day.date) {
                return <Box key={dayIndex} sx={{ ...cellSx, backgroundColor: 'transparent', border: 'none' }} />;
              }

              return (
                <Tooltip
                  key={day.date}
                  title={
                    <Box>
                      <Typography variant="caption" fontWeight={600}>
                        {day.count} events
                      </Typography>
                      <Typography variant="caption" display="block">
                        {formatDate(day.date)}
                      </Typography>
                    </Box>
                  }
                  arrow
                >
                  <Paper
                    elevation={0}
                    sx={{
                      ...cellSx,
                      backgroundColor: getColor(day.count),
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      '&:hover': {
                        transform: 'scale(1.15)',
                        zIndex: 10,
                        boxShadow: 1,
                      },
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        ))}
      </Box>

      {/* Legend */}
      <Box display="flex" alignItems="center" gap={0.75} mt={2}>
        <Typography variant="caption" color="text.secondary">
          Less
        </Typography>
        {[0, 0.2, 0.4, 0.6, 0.8, 1].map((intensity, index) => (
          <Paper
            key={index}
            elevation={0}
            sx={{
              width: 12,
              height: 12,
              backgroundColor: getColor(intensity * maxCount),
              border: '1px solid rgba(0,0,0,0.08)',
              borderRadius: '2px',
            }}
          />
        ))}
        <Typography variant="caption" color="text.secondary">
          More
        </Typography>
      </Box>
    </Box>
  );
};
